import type { StateCreator } from 'zustand'
import { AuthService } from '@/services/auth'
import type { LoginCredentials, User } from '@/types/user'

export interface AuthState {
	token: string
	user: User | null
	loading: boolean
	setToken: (token: string) => void
	setUser: (user: User | null) => void
	setLoading: (loading: boolean) => void
	login: (credentials: LoginCredentials) => Promise<void>
	logout: () => void
	initialize: () => Promise<void>
	isAuthenticated: () => boolean
	isAdmin: () => boolean
	isManager: () => boolean
	isEmployee: () => boolean
}

export const createAuthState: StateCreator<AuthState> = (set, get) => ({
	token: '',
	user: null,
	loading: true,

	setToken: (token) => set(() => ({ token })),
	setUser: (user) => set(() => ({ user })),
	setLoading: (loading) => set(() => ({ loading })),

	login: async (credentials) => {
		try {
			set({ loading: true })
			const response = await AuthService.login(credentials)

			// La respuesta ya está validada en AuthService, solo extraemos el data
			const userData = response.data

			if (!userData) {
				throw new Error('No se recibieron datos del usuario')
			}

			// Si la API devuelve un token en el futuro, lo guardaríamos aquí
			// const token = response.token || "";
			set({
				user: userData,
				token: '', // Por ahora no hay token
				loading: false,
			})
		} catch (error) {
			set({ loading: false })
			throw error
		}
	},

	logout: () => {
		AuthService.logout()
		set({
			token: '',
			user: null,
		})
	},

	initialize: async () => {
		const state = get()
		set({ loading: true })

		// Usar el usuario del store (ya restaurado por persist) o de localStorage
		const currentUser = state.user || AuthService.getUser()

		// Si no hay usuario en el store pero hay en localStorage, restaurarlo
		// Esto es necesario para que el interceptor pueda añadir los headers
		if (!state.user && currentUser) {
			set({ user: currentUser })
		}

		// Si hay usuario, intentar refrescar sesión
		if (currentUser) {
			// Asegurarnos de que el usuario esté en el store antes de hacer la petición
			// para que el interceptor pueda añadir los headers correctamente
			if (!state.user) {
				set({ user: currentUser })
			}

			try {
				const response = await AuthService.refreshSession()
				const userData = response.data

				if (userData) {
					// Actualizar datos del usuario
					set({
						user: userData,
						loading: false,
					})
					// Guardar usuario actualizado en localStorage
					AuthService.saveUser(userData)
				} else {
					// Si la respuesta es null, cerrar sesión
					AuthService.logout()
					set({
						user: null,
						token: '',
						loading: false,
					})
				}
			} catch (_error) {
				// Si hay error al refrescar, cerrar sesión
				AuthService.logout()
				set({
					user: null,
					token: '',
					loading: false,
				})
			}
		} else {
			// No hay usuario guardado, solo marcar como no loading
			set({ loading: false })
		}
	},

	isAuthenticated: () => {
		const state = get()
		return !!state.user || !!state.token
	},

	isAdmin: () => {
		const state = get()
		return state.user?.isAdmin ?? false
	},

	isManager: () => {
		const state = get()
		return state.user?.isManager ?? false
	},

	isEmployee: () => {
		const state = get()
		return state.user?.isEmployee ?? false
	},
})
